import React, { useEffect, useState, useMemo, useCallback } from 'react'

import { ArrowLeft } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'

import { useTranslation } from '@/core/i18n/useTranslation'
import { BillingInformationFormData } from '@/features/account/billing'
import BillingInformation from '@/features/account/billing/components/BillingInformation'
import AuthenticatedLayout from '@/shared/components/organisms/Layout/AuthenticatedLayout/AuthenticatedLayout'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

import {
	SubscriptionStepper,
	type SubscribeState,
} from './components/SubscriptionStepper'
import { SubscriptionSummary } from './components/SubscriptionSummary'
import { useMobileSubscribeStepScrollTop } from './hooks/useMobileSubscribeStepScrollTop'
import { useSubscribeGuard } from './hooks/useSubscribeGuard'
import { BillingCycle, PlanConfig, PlanType } from './types'

// Default plan config for the summary (will be passed via navigation state)
const DEFAULT_PLAN: PlanConfig = {
	id: 'essential',
	name: 'Essential',
	description: '',
	priceMonthly: 15,
	priceAnnual: 12,
	currency: 'USD',
	hasZoneSelector: true,
}

interface LocationState {
	// Can be either the full PlanConfig (current flow) or a plan id/name string (stepper draft)
	plan?: PlanConfig | string
	billingCycle?: BillingCycle | string
	zonesCount?: number
	termsAccepted?: boolean
	// Allow carrying arbitrary draft data between steps
	[key: string]: unknown
}

const BillingInformationPage: React.FC = () => {
	useSubscribeGuard()
	useMobileSubscribeStepScrollTop()
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const location = useLocation()
	const account = useCurrentAccount()
	const [isSavingBilling, setIsSavingBilling] = useState(false)

	// Get state from navigation
	const state = location.state as LocationState | null

	// If no state (page reload), redirect to step 1
	useEffect(() => {
		if (!state || !state.plan) {
			navigate('/subscribe', { replace: true })
		}
	}, [state, navigate])
	const initialBillingDraft = state?.billingDraft as
		| BillingInformationFormData
		| undefined

	const rawPlan = state?.plan
	const plan: PlanConfig =
		rawPlan && typeof rawPlan === 'object' && 'id' in rawPlan
			? (rawPlan as PlanConfig)
			: typeof rawPlan === 'string'
				? { ...DEFAULT_PLAN, id: rawPlan as PlanType, name: rawPlan }
				: DEFAULT_PLAN

	const billingCycle: BillingCycle =
		(state?.billingCycle as BillingCycle) || 'annual'

	// Get current subscription's zone quantity as fallback
	const currentSubscriptionZones = useMemo(() => {
		if (!account?.subscriptions) return 1

		const activeSubscription = account.subscriptions.find(
			(sub) => sub.status === 'active' || sub.status === 'in_trial'
		)

		if (!activeSubscription) return 1

		const playerQuantity = activeSubscription.getPlayerQuantity()
		return playerQuantity || 1
	}, [account?.subscriptions])

	const zonesCount = state?.zonesCount || currentSubscriptionZones

	const [termsAccepted, setTermsAccepted] = useState(
		state?.termsAccepted || false
	)

	// Local draft for billing form to survive navigation between steps
	const [billingDraft, setBillingDraft] = useState<
		BillingInformationFormData | undefined
	>(initialBillingDraft)

	// Memoize the draft change handler to prevent infinite loops
	const handleDraftChange = useCallback((data: BillingInformationFormData) => {
		setBillingDraft(data)
	}, [])

	// Minimal draft used by the stepper when navigating between steps
	const stepperDraft: SubscribeState = {
		plan: plan.id,
		billingCycle,
		zonesCount,
		billingDraft,
	}

	// Full navigation state keeps any extra fields from previous steps
	const navigationState = {
		...(state ?? {}),
		plan,
		billingCycle,
		zonesCount,
		billingDraft,
		termsAccepted,
	}

	const handleBack = () => {
		navigate('/subscribe', {
			state: navigationState,
		})
	}

	const handleContinue = () => {
		// Navigate to payment step
		navigate('/subscribe/payment', {
			state: navigationState,
		})
	}

	const FORM_ID = 'billing-information-form'

	return (
		<AuthenticatedLayout hideSidebar topAppBarContent={<></>}>
			<div className="min-h-screen bg-background">
				{/* Header */}
				<div className="border-b border-border">
					<div className="max-w-7xl mx-auto px-6 py-4">
						<div className="flex items-center gap-4">
							<button
								onClick={handleBack}
								aria-label={t('actions.back')}
								className="text-muted-foreground hover:text-foreground transition-colors"
							>
								<ArrowLeft className="h-5 w-5" />
							</button>
							<h1 className="text-xl font-medium text-muted-foreground">
								{t('subscribe.title')}
								{/* <span className="text-foreground"> | </span>
								<span className="text-foreground">
									{t('subscribe.steps.billing')}
								</span> */}
							</h1>
						</div>
					</div>
				</div>

				{/* Stepper */}
				<div className="border-b border-border py-6 hidden lg:block">
					<SubscriptionStepper currentStep="billing" draft={stepperDraft} />
				</div>

				{/* Main Content */}
				<div className="max-w-7xl mx-auto px-6 py-8">
					<div className="flex gap-8 flex-col lg:flex-row">
						{/* Left Column - Billing Form */}
						<div className="flex-1">
							<BillingInformation
								onBack={handleBack}
								hideHeader
								formId={FORM_ID}
								onSuccess={handleContinue}
								hideActionButtons
								initialValues={billingDraft}
								onDraftChange={handleDraftChange}
								onSavingChange={setIsSavingBilling}
							/>
						</div>

						{/* Right Column - Summary */}
						<div className="w-full lg:w-80 flex-shrink-0">
							<div className="sticky top-8">
								<SubscriptionSummary
									plan={plan}
									billingCycle={billingCycle}
									zonesCount={zonesCount}
									onContinue={handleContinue}
									formId={FORM_ID}
									isLoading={isSavingBilling}
									isTermsAccepted={termsAccepted}
									onTermsAcceptedChange={setTermsAccepted}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</AuthenticatedLayout>
	)
}

export default BillingInformationPage
